import React from 'react';
import { Box } from '@mui/material';
import {
  MicNone as MicIcon,
  PhotoCameraOutlined as PhotoIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Container, Lead, Section, SectionTitle } from './primitives';
import { landing } from './tokens';

type Tone = 'solid' | 'plain' | 'soft';

// A fixed rhythm of bubble styles, so the wall reads like a conversation.
const tones: Tone[] = ['solid', 'plain', 'plain', 'soft', 'plain', 'solid', 'plain', 'soft', 'plain', 'plain'];

const toneSx: Record<Tone, object> = {
  solid: { bgcolor: landing.accent, color: '#FFFFFF' },
  plain: { bgcolor: landing.surface, border: `1px solid ${landing.line}` },
  soft: {
    bgcolor: landing.accentSoft,
    border: `1px solid ${landing.accentSoft2}`,
  },
};

const bubbleSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  px: 2,
  py: 1.25,
  borderRadius: '18px 18px 5px 18px',
  fontSize: 15.5,
  '& svg': { fontSize: 17 },
};

const MessageExamples: React.FC = () => {
  const { t } = useTranslation('landing');
  const items = t('examples.items', { returnObjects: true }) as unknown as string[];
  return (
    <Section sx={{ pt: { xs: 5, md: 7 }, pb: { xs: 9, md: 14 } }}>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4.5,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.75,
            maxWidth: 720,
          }}
        >
          <SectionTitle sx={{ fontSize: 'clamp(30px, 3.6vw, 46px)', lineHeight: 1.06 }}>
            {t('examples.title')}
          </SectionTitle>
          <Lead>{t('examples.subtitle')}</Lead>
        </Box>
        <Box
          component='ul'
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 1.5,
            maxWidth: 980,
          }}
        >
          {items.map((text, index) => (
            <Box
              key={text}
              component='li'
              sx={{ ...bubbleSx, ...toneSx[tones[index % tones.length]] }}
            >
              {text}
            </Box>
          ))}
          <Box component='li' sx={{ ...bubbleSx, ...toneSx.solid }}>
            <MicIcon />
            {t('examples.voice')}
          </Box>
          <Box component='li' sx={{ ...bubbleSx, ...toneSx.plain }}>
            <PhotoIcon />
            {t('examples.photo')}
          </Box>
        </Box>
      </Container>
    </Section>
  );
};

export default MessageExamples;
